/**
 * Mobile-internal Free Agent Search page.
 * Per Tony's mobile architecture rule + 9o arc closure (9o.7 cutover):
 * - All hooks come from the mobile-internal FA hooks barrel
 * - All modals come from mobile-internal components
 * - useRosterPlan is the only shared (non-Mobile*-prefixed) hook, formally
 *   promoted to hooks/rosterPlan/ per 9o.5 — mobile + desktop both consume
 *   the canonical shared hook (re-exported through the mobile FA barrel).
 *
 * ZERO imports from pages/league-dashboard/ or components/league-dashboard/
 * or components/modals/. Source-inspection regression test pins this contract
 * (MobileFreeAgentSearch.architecture.test.js).
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dynastyTheme } from '../../../services/colorService';

import { leaguesAPI } from '../../../services/apiService';
import { formatApiError } from '../../../services/errorUtils';
import { useCommissioner } from '../../../contexts/CommissionerContext';
import { useAssignmentLeagueConfig } from '../../../hooks/useAssignmentLeagueConfig';
// 9o.3: hook consumer migrated together with provider — desktop's useBatchSelection
// reads desktop's Context, which is no longer provided. Same migration unit.
import { useMobileBatchSelection } from './free-agent-search/MobileBatchSelectionProvider';
import {
  canAddPlayers,
  canClaimWaiverPlayer,
  findBestPositionForPlayer
} from '../../../utils/RosterCapacityUtils';

// Mobile-internal hooks (9o.7 cutover — closes the 9o arc).
// useRosterPlan is re-exported through this barrel from the shared hooks/rosterPlan/
// location (9o.5 promotion) — single import path for all 5 hooks.
import {
  useMobileSearchFilters,
  useMobileLeagueContext,
  useMobilePlayerData,
  useMobilePlayerAssignment,
  useRosterPlan,
} from './free-agent-search/hooks';

// Mobile-specific components.
import { MobileBatchSelectionProvider } from './free-agent-search/MobileBatchSelectionProvider';
import MobileSearchControls from './free-agent-search/MobileSearchControls';
import MobileStatusBanners from './free-agent-search/MobileStatusBanners';
import MobilePlayerTable from './free-agent-search/MobilePlayerTable';
import MobileBulkAddBanner from './free-agent-search/MobileBulkAddBanner';
import { MobilePlayerModal, MobilePlayerCard } from '../components/player';
import MobileFASwapSheet from './optimizer/MobileFASwapSheet';

// Position assignment + bulk + drop-designation modals (mobile-internal, 9o.6.a + 9o.7).
import MobilePositionAssignmentModal from '../components/MobilePositionAssignmentModal';
import MobileRosterPlanModal from '../components/MobileRosterPlanModal';
import MobileBulkPositionAssignmentModal from '../components/MobileBulkPositionAssignmentModal';
import MobileDropDesignationModal from '../components/MobileDropDesignationModal';
// DD-MODAL-0723: tell the modal's ownership-bearing session cache the world changed.
import { invalidatePlayer, invalidateLeaguePlayers } from '../components/player/hooks/playerDataCache';

// ========================================
// WRAPPER COMPONENT WITH BATCH PROVIDER
// ========================================
const MobileFreeAgentSearch = ({ leagueId, league, user }) => {
  return (
    <MobileBatchSelectionProvider>
      <MobileFreeAgentSearchInner
        leagueId={leagueId}
        league={league}
        user={user}
      />
    </MobileBatchSelectionProvider>
  );
};

// ========================================
// INNER COMPONENT WITH HOOKS
// ========================================
const MobileFreeAgentSearchInner = ({ leagueId, league, user }) => {
  const navigate = useNavigate();
  const {
    isCommissionerMode,
    activeTeamId,
    activeTeamName,
    getTargetTeamId,
    isCommissioner
  } = useCommissioner();

  // Access batch selection context
  const { getSelectedPlayerObjects, clearAllSelections } = useMobileBatchSelection();

  // Player modal state
  const [isPlayerModalOpen, setIsPlayerModalOpen] = React.useState(false);
  const [isPlayerCardOpen, setIsPlayerCardOpen] = React.useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = React.useState(null);

  // DD-RO-MOBILE-SWAP-0727: player-initiated 1-1 swap (FA direction). The FA detail sheet emits onSwap(fa);
  // reuse MobileDropDesignationModal to pick the roster player to drop, then open MobileFASwapSheet with
  // {player: fa, drop}, which executes via the canonical generatePlanForDrop + executePlan. No new mutation.
  const [swapCandidate, setSwapCandidate] = React.useState(null);   // the FA being swapped in
  const [swapDropOpen, setSwapDropOpen] = React.useState(false);    // the drop-picker
  const [swapRec, setSwapRec] = React.useState(null);               // {player, drop} handed to the sheet
  // DD-RO-FA-SEARCH-TDZ-CRASH-0729: handleSwapInitiate/handleSwapDropConfirm are declared BELOW, after
  // `positionAssignment` — they read it in their deps arrays, and a deps array is evaluated at the useCallback
  // CALL SITE during render, so declaring them here (above the line-153 `const positionAssignment`) hit its
  // temporal dead zone ("Cannot access 'positionAssignment' before initialization") the moment this screen
  // rendered. Moved past the declaration.

  // Canonical league config for assignment surfaces (DD-FIXES-0705 Fix 2):
  // ONE source, §5 nested shape, fail-loud. The old hand-merged
  // fullLeagueDetails spread (flat position_requirements at top level) and
  // its catch-fallback to a parent prop of unknown shape are dead.
  const {
    league: assignmentLeague,
    error: leagueConfigError,
  } = useAssignmentLeagueConfig(leagueId);

  // Waiver claim modal state — opened when user taps Claim on a waiver player.
  // Separate from the position assignment modal; the claim flow goes through
  // waivers/claims API, not addPlayerToTeam.
  const [showWaiverModal, setShowWaiverModal] = React.useState(false);
  const [waiverPlayerPending, setWaiverPlayerPending] = React.useState(null);
  // Phase A.4.5: when true, the RosterPlanModal's onConfirm submits a waiver
  // claim with the chosen plan (queue semantic) instead of executing it.
  const [isWaiverPlanQueueMode, setIsWaiverPlanQueueMode] = React.useState(false);

  // ========================================
  // CUSTOM HOOKS (MOBILE-INTERNAL per 9o.7)
  // ========================================
  const searchFilters = useMobileSearchFilters({ includeNon40Man: !!isCommissionerMode });
  const leagueContext = useMobileLeagueContext(leagueId, isCommissionerMode);

  // Sync includeNon40Man when Commissioner Mode is toggled while on page
  React.useEffect(() => {
    searchFilters.setIncludeNon40Man(!!isCommissionerMode);
  }, [isCommissionerMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // DD-FASEARCH-RACE-0730: pass the AUTHORITATIVE isCommissionerMode so the fetch derives
  // include_non_40man at query construction, not the render-late setIncludeNon40Man effect above.
  const playerData = useMobilePlayerData(leagueId, searchFilters, leagueContext, isCommissionerMode);
  
  // Fetch full league details for position assignment modal
  
  const positionAssignment = useMobilePlayerAssignment(leagueId, assignmentLeague, {
    transactionsEnabled: leagueContext.transactionsEnabled,
    leagueStatus: leagueContext.leagueStatus,
    isCommissionerMode,
    activeTeamName,
    getTargetTeamId
  });

  // DD-RO-FA-SEARCH-TDZ-CRASH-0729: declared HERE (after `positionAssignment`) so the deps arrays that read it
  // are evaluated post-initialization — the swap state lives up with the other useState above.
  const handleSwapInitiate = useCallback(async (fa) => {
    if (!positionAssignment.rosterCapacityAnalysis) {
      await positionAssignment.loadRosterCapacity();
    }
    setSwapCandidate(fa);
    setSwapDropOpen(true);
  }, [positionAssignment]);
  const handleSwapDropConfirm = useCallback((dropId) => {
    const roster = positionAssignment.rosterCapacityAnalysis?.players || [];
    const dp = roster.find((p) => p?.ids?.league_player === dropId
      || String(p?.ids?.mlb) === String(dropId) || p?.ids?.mlb === dropId);
    setSwapDropOpen(false);
    if (swapCandidate && dp) {
      setSwapRec({ player: swapCandidate, drop: { player_id: dp.ids?.mlb, player_name: dp.info?.full_name } });
    }
    setSwapCandidate(null);
  }, [positionAssignment, swapCandidate]);

  // Phase 2.3: drop-shuffle plan flow (mobile parity).
  // Per ROSTER_PLAN_SPEC.md § B.5.7. useRosterPlan was formally promoted to
  // hooks/rosterPlan/ in 9o.5 — same state machine + API calls shared with
  // desktop (mobile and desktop now consume the canonical hook). Re-exported
  // through the mobile FA hooks barrel for single-import convenience.
  // MobileRosterPlanModal renders this state.
  const rosterPlan = useRosterPlan({
    leagueId,
    candidate: positionAssignment.playerForAssignment,
    currentRoster: positionAssignment.currentRoster,
    isCommissionerMode,
    getTargetTeamId,
  });

  // ========================================
  // PLAYER ACTION HANDLERS
  // ========================================
  const handleAddPlayer = useCallback(async (player) => {
    // Check if transactions are allowed
    const isOfflineDraft = leagueContext.leagueStatus === 'offline_draft';
    const isDraftMode = ['draft', 'offline_draft'].includes(leagueContext.leagueStatus);

    if (!isOfflineDraft && !isDraftMode && !isCommissionerMode && !leagueContext.transactionsEnabled) {
      playerData.setError('Transactions are not allowed until the commissioner sets player prices');
      setTimeout(() => playerData.setError(''), 5000);
      return;
    }

    // Check ownership
    const isOwned = player.league_context?.team?.team_name;

    if (isOwned) {
      playerData.setError('Player is already owned by a team');
      setTimeout(() => playerData.setError(''), 5000);
      return;
    }

    // Waiver branch: on-waivers players go through the waiver claim flow,
    // not the direct add flow (which the backend rejects).
    const onWaivers =
      player.league_context?.availability?.on_waivers === true ||
      player.league_context?.availability?.status === 'waivers';
    if (onWaivers) {
      // Load roster capacity for the drop-designation picker
      if (!positionAssignment.rosterCapacityAnalysis) {
        await positionAssignment.loadRosterCapacity();
      }
      setWaiverPlayerPending(player);
      setShowWaiverModal(true);
      return;
    }

    // Load roster capacity if not already loaded (needed for modal to show available slots)
    if (!positionAssignment.rosterCapacityAnalysis) {
      await positionAssignment.loadRosterCapacity();
    }

    // Always open the modal - let it handle showing available options (active, bench, minors, DL)
    // The modal will check for available slots for each type and show warnings for DL/Minors
    positionAssignment.setPlayerForAssignment(player);
    positionAssignment.setShowPositionModal(true);
  }, [leagueContext.transactionsEnabled, leagueContext.leagueStatus, isCommissionerMode, positionAssignment, league, playerData]);

  const submitWaiverClaimRequest = useCallback(async (claimBody, claimedPlayer) => {
    try {
      const res = await leaguesAPI.submitWaiverClaim(leagueId, claimBody);
      invalidatePlayer(leagueId, claimedPlayer?.ids?.mlb);
      const data = res?.data || res;
      if (data?.success) {
        const playerName = claimedPlayer?.info?.full_name
          || `${claimedPlayer?.info?.first_name || ''} ${claimedPlayer?.info?.last_name || ''}`.trim()
          || 'player';
        playerData.setSuccessMessage(`Waiver claim submitted for ${playerName}.`);
        setTimeout(() => playerData.setSuccessMessage(''), 5000);
        return true;
      }
      const msg = data?.message || data?.error || 'Failed to submit waiver claim.';
      playerData.setError(msg);
      setTimeout(() => playerData.setError(''), 6000);
      return false;
    } catch (err) {
      playerData.setError(formatApiError(err, 'Failed to submit waiver claim.'));
      setTimeout(() => playerData.setError(''), 6000);
      return false;
    }
  }, [leagueId, playerData]);

  const handleWaiverClaimConfirm = useCallback(async (dropPlayerId) => {
    if (!waiverPlayerPending) return;
    setShowWaiverModal(false);

    const leaguePlayerId = waiverPlayerPending.ids?.league_player;
    if (!leaguePlayerId) {
      playerData.setError('Cannot claim: player has no league_player id.');
      setTimeout(() => playerData.setError(''), 5000);
      setWaiverPlayerPending(null);
      return;
    }
    const claimedPlayer = waiverPlayerPending;
    await submitWaiverClaimRequest({
      player_league_id: leaguePlayerId,
      drop_player_league_id: dropPlayerId || null,
    }, claimedPlayer);
    setWaiverPlayerPending(null);
  }, [waiverPlayerPending, playerData, submitWaiverClaimRequest]);

  // Phase A.4.5: queue a roster-shuffle plan with a waiver claim. Invoked by
  // RosterPlanModal's onConfirm when isWaiverPlanQueueMode is true.
  const handleWaiverPlanQueueConfirm = useCallback(async () => {
    if (!waiverPlayerPending || !rosterPlan.selectedPlan || !rosterPlan.selectedDropPlayer) {
      return null;
    }
    const leaguePlayerId = waiverPlayerPending.ids?.league_player;
    const dropLeaguePlayerId = rosterPlan.selectedDropPlayer.ids?.league_player;
    const planCandidateMlb = waiverPlayerPending.ids?.mlb;
    const planDropMlb = rosterPlan.selectedDropPlayer.ids?.mlb;
    if (!leaguePlayerId || !dropLeaguePlayerId || planCandidateMlb == null || planDropMlb == null) {
      playerData.setError('Cannot queue plan: missing identifiers on waiver or drop player.');
      setTimeout(() => playerData.setError(''), 5000);
      return null;
    }
    const ok = await submitWaiverClaimRequest({
      player_league_id: leaguePlayerId,
      drop_player_league_id: dropLeaguePlayerId,
      plan_fingerprint: rosterPlan.selectedPlan.plan_fingerprint,
      plan_candidate_player_id: planCandidateMlb,
      plan_drop_player_id: planDropMlb,
    }, waiverPlayerPending);
    rosterPlan.reset();
    positionAssignment.setPlayerForAssignment(null);
    setIsWaiverPlanQueueMode(false);
    setWaiverPlayerPending(null);
    return ok ? { success: true } : null;
  }, [
    waiverPlayerPending,
    rosterPlan,
    playerData,
    submitWaiverClaimRequest,
    positionAssignment,
  ]);

  // Modal "Use roster move plan" button → close waiver modal, open
  // RosterPlanModal in queue mode with the waiver player as candidate.
  const handleWaiverRequestPlan = useCallback(() => {
    if (!waiverPlayerPending) return;
    setShowWaiverModal(false);
    positionAssignment.setPlayerForAssignment(waiverPlayerPending);
    setIsWaiverPlanQueueMode(true);
    rosterPlan.open();
  }, [waiverPlayerPending, positionAssignment, rosterPlan]);

  const executePlayerAssignment = useCallback(async (player, assignment) => {
    const firstName = player.info?.first_name;
    const lastName = player.info?.last_name;
    const leaguePlayerId = player.ids?.league_player;

    positionAssignment.setAddingPlayer(leaguePlayerId);
    playerData.setError('');

    try {
      // Bible Rule 9: missing required data is a bug — throw, not fallback.
      if (!leaguePlayerId) {
        throw new Error(
          `Cannot add player: missing ids.league_player (mlb=${player.ids?.mlb}, ` +
          `name=${player.info?.full_name}). Player object likely came from non-canonical source.`
        );
      }
      if (!assignment?.type) {
        throw new Error('Cannot add player: assignment.type is required');
      }
      if (assignment.type === 'active' && !assignment.slotId) {
        throw new Error(
          `Cannot add ${player.info?.full_name} to active roster: missing assignment.slotId`
        );
      }

      // 4-branch contract resolution per Bible § 4.5 contract model:
      //   - Minors: zero out
      //   - Offseason w/ projected_contract (line 1027): keeper rights
      //   - Owned in-season: existing roster_data.financial
      //   - FA pickup (default): baseline_price + league policy (§ 4.5.1 line 809, § 4.5.2 line 821)
      const isOffseason = ['setup', 'draft', 'finalizing'].includes(leagueContext.leagueStatus);
      const isOwnedAlready = player.league_context?.status === 'owned';
      const isMinors = assignment.type === 'minors';
      const playerName = player.info?.full_name;
      const effectiveLeague = assignmentLeague; // canonical §5 shape from useAssignmentLeagueConfig

      let playerSalary;
      let contractYears;
      if (isMinors) {
        playerSalary = 0;
        contractYears = 0;
      } else if (isOffseason && player.projected_contract) {
        playerSalary = player.projected_contract.baseline_price;
        contractYears = player.projected_contract.contract_years;
        if (playerSalary == null) {
          throw new Error(`Cannot add ${playerName}: missing projected_contract.baseline_price`);
        }
        if (contractYears == null) {
          throw new Error(`Cannot add ${playerName}: missing projected_contract.contract_years`);
        }
      } else if (isOwnedAlready) {
        playerSalary = player.league_context.financial?.contract_salary;
        contractYears = player.league_context.financial?.contract_years;
        if (playerSalary == null) {
          throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_salary on owned player`);
        }
        if (contractYears == null) {
          throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_years on owned player`);
        }
      } else {
        // Bible § 4.5.1 line 809 + § 10 line 1701: FA pickup price = baseline_price.
        // Bible § 4.5.2 line 821: contract_years = league.salary_settings.standard_contract_length.
        playerSalary = player.league_context?.financial?.baseline_price;
        contractYears = effectiveLeague?.salary_settings?.standard_contract_length;
        if (playerSalary == null) {
          throw new Error(
            `Cannot add ${playerName}: missing baseline_price on league_context.financial. ` +
            `Pricing engine may not have run for this league.`
          );
        }
        if (contractYears == null) {
          throw new Error(
            `Cannot add ${playerName}: league.salary_settings.standard_contract_length is not configured.`
          );
        }
      }

      const playerDataPayload = {
        league_player_id: leaguePlayerId,
        salary: playerSalary,
        contract_years: contractYears,
        roster_status: assignment.type,
        roster_position: assignment.type === 'active' ? assignment.slotId : null,
        start_contract: !isMinors
      };

      if (isCommissionerMode) {
        playerDataPayload.commissioner_action = true;
        playerDataPayload.target_team_id = getTargetTeamId(null);
      }

      const response = await leaguesAPI.addPlayerToTeam(leagueId, playerDataPayload);
          invalidateLeaguePlayers(leagueId);

      if (response.success) {
        const teamName = isCommissionerMode ? activeTeamName : 'your team';
        const positionText = assignment.type === 'active' ? assignment.position.toUpperCase() : assignment.type;
        playerData.setSuccessMessage(`${response.player_name || firstName + ' ' + lastName} added to ${teamName} (${positionText})!`);

        if (!searchFilters.showAll) {
          playerData.setPlayers(prev => prev.filter(p => {
            const pId = p.ids?.league_player;
            return pId !== leaguePlayerId;
          }));
          playerData.setTotalCount(prev => prev - 1);
        } else {
          playerData.loadPlayers(0, false);
        }

        await positionAssignment.loadRosterCapacity();
        window.dispatchEvent(new Event('roster-updated'));

        setTimeout(() => {
          playerData.setSuccessMessage('');
        }, 3000);

      } else {
        const errorMsg = response.message || response.error?.message || response.error || 'Failed to add player';
        playerData.setError(errorMsg);
      }
    } catch (err) {
      // 9o.6.d-followup: formatApiError replaces inline JSON.stringify + cargo-cult
      // err.response.data.message check. Same fix as desktop FA + Mobile Stats.
      playerData.setError(formatApiError(err, 'Failed to add player'));
    } finally {
      positionAssignment.setAddingPlayer(null);
      positionAssignment.setShowPositionModal(false);
      positionAssignment.setPlayerForAssignment(null);
    }
  }, [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, searchFilters.showAll, playerData, positionAssignment]);

  const handleBatchAddPlayers = useCallback(async (selectedPlayers) => {
    if (!leagueContext.transactionsEnabled) {
      playerData.setError('Transactions are not allowed until prices are set');
      return;
    }

    if (selectedPlayers.length === 0) {
      playerData.setError('No players selected');
      return;
    }

    if (!positionAssignment.rosterCapacityAnalysis) {
      await positionAssignment.loadRosterCapacity();
      playerData.setError('Checking roster capacity...');
      setTimeout(() => playerData.setError(''), 3000);
      return;
    }

    const capacityCheck = canAddPlayers(selectedPlayers.length, positionAssignment.rosterCapacityAnalysis);
    if (!capacityCheck.canAdd) {
      playerData.setError(`Cannot add ${selectedPlayers.length} players: ${capacityCheck.message}`);
      setTimeout(() => playerData.setError(''), 8000);
      return;
    }

    positionAssignment.setShowBulkAssignmentModal(true);
  }, [leagueContext.transactionsEnabled, positionAssignment, playerData]);

  const handleBulkAssignmentComplete = useCallback(async (assignmentData) => {
    playerData.setError('');

    const results = {
      successful: [],
      failed: []
    };

    try {
      for (const assignment of assignmentData) {
        try {
          const leaguePlayerId = assignment.player.ids?.league_player;
          const playerDataPayload = {
            league_player_id: leaguePlayerId,
            salary: assignment.salary,
            contract_years: assignment.contract_years,
            roster_status: assignment.roster_status,
            roster_position: assignment.roster_position,
            start_contract: assignment.start_contract
          };

          if (isCommissionerMode) {
            playerDataPayload.commissioner_action = true;
            playerDataPayload.target_team_id = getTargetTeamId(null);
          }

          const response = await leaguesAPI.addPlayerToTeam(leagueId, playerDataPayload);
          invalidateLeaguePlayers(leagueId);

          if (response.success) {
            const firstName = assignment.player.info?.first_name;
            const lastName = assignment.player.info?.last_name;
            results.successful.push({
              player: assignment.player,
              name: response.player_name || `${firstName} ${lastName}`,
              position: assignment.roster_status === 'active' ? assignment.roster_position : assignment.roster_status
            });
          } else {
            const firstName = assignment.player.info?.first_name;
            const lastName = assignment.player.info?.last_name;
            results.failed.push({
              player: assignment.player,
              name: `${firstName} ${lastName}`,
              error: response.message || 'Unknown error'
            });
          }
        } catch (playerErr) {
          const firstName = assignment.player.info?.first_name;
          const lastName = assignment.player.info?.last_name;
          results.failed.push({
            player: assignment.player,
            name: `${firstName} ${lastName}`,
            error: playerErr.message || 'Network error'
          });
        }
      }

      if (results.successful.length > 0) {
        const teamName = isCommissionerMode ? activeTeamName : 'your team';
        playerData.setSuccessMessage(`Successfully added ${results.successful.length} players to ${teamName}!`);

        clearAllSelections();
        await positionAssignment.loadRosterCapacity();
        window.dispatchEvent(new Event('roster-updated'));

        setTimeout(() => {
          playerData.setSuccessMessage('');
        }, 3000);
      }

      if (results.failed.length > 0) {
        const failedNames = results.failed.map(f => f.name).join(', ');
        playerData.setError(`Failed to add: ${failedNames}`);
      }

    } catch (err) {
      console.error('Error in bulk assignment completion:', err);
      // 9o.6.d-followup: folded in for parity with desktop FA bulk-add (was hardcoded
      // 'Bulk assignment failed' regardless of cause; now surfaces backend detail).
      playerData.setError(formatApiError(err, 'Bulk assignment failed'));
    } finally {
      positionAssignment.setShowBulkAssignmentModal(false);
    }
  }, [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, clearAllSelections, positionAssignment, playerData]);

  const handlePlayerClick = useCallback((player) => {
    const playerId = player.ids?.mlb;
    setSelectedPlayerId(playerId?.toString());
    setIsPlayerModalOpen(true);
  }, []);

  // Modal handlers
  const handleExpandToCard = () => {
    setIsPlayerModalOpen(false);
    setIsPlayerCardOpen(true);
  };

  const handleCollapseToModal = () => {
    setIsPlayerCardOpen(false);
    setIsPlayerModalOpen(true);
  };

  const handleClosePlayer = () => {
    setIsPlayerModalOpen(false);
    setIsPlayerCardOpen(false);
    setSelectedPlayerId(null);
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (!leagueContext.isReady) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        backgroundColor: 'transparent',
        minHeight: '100vh'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: `3px solid ${dynastyTheme.tokens.colors.neutral[700]}`,
          borderTop: `3px solid ${dynastyTheme.tokens.colors.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 15px'
        }}></div>
        <p style={{ color: dynastyTheme.tokens.colors.neutral[400] }}>
          Loading league settings...
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // ========================================
  // STATE OBJECT FOR CHILD COMPONENTS
  // ========================================
  const freeAgentState = {
    // Player Data
    players: playerData.players,
    totalCount: playerData.totalCount,
    loading: playerData.loading,
    backgroundLoading: playerData.backgroundLoading,
    hasMore: playerData.hasMore,
    page: playerData.page,
    error: playerData.error,
    setError: playerData.setError,
    successMessage: playerData.successMessage,
    setSuccessMessage: playerData.setSuccessMessage,
    loadPlayers: playerData.loadPlayers,

    // Search Filters
    searchTerm: searchFilters.searchTerm,
    setSearchTerm: searchFilters.setSearchTerm,
    activeTab: searchFilters.activeTab,
    setActiveTab: searchFilters.setActiveTab,
    positionFilter: searchFilters.positionFilter,
    setPositionFilter: searchFilters.setPositionFilter,
    viewMode: searchFilters.viewMode,
    setViewMode: searchFilters.setViewMode,
    showAll: searchFilters.showAll,
    setShowAll: searchFilters.setShowAll,
    includeNon40Man: searchFilters.includeNon40Man,
    setIncludeNon40Man: searchFilters.setIncludeNon40Man,
    // 9m Root Cause #1: pre-9m these were missing from freeAgentState, so MobileSearchControls
    // destructured `setAdvancedFilters` as undefined → TypeError on Apply → all 11 advanced
    // filters silently broken. Pass-through unblocks the entire advanced filter drawer.
    advancedFilters: searchFilters.advancedFilters,
    setAdvancedFilters: searchFilters.setAdvancedFilters,
    // Recent toggle — rolling-row visibility.
    showRecent: searchFilters.showRecent,
    setShowRecent: searchFilters.setShowRecent,

    // League Context
    leagueStatus: leagueContext.leagueStatus,
    transactionsEnabled: leagueContext.transactionsEnabled,
    browseMode: leagueContext.browseMode,
    noPricesWarning: leagueContext.noPricesWarning,
    pricesExist: leagueContext.pricesExist,
    handleBrowseAnyway: leagueContext.handleBrowseAnyway,

    // Position Assignment
    addingPlayer: positionAssignment.addingPlayer,
    rosterCapacityAnalysis: positionAssignment.rosterCapacityAnalysis,

    // Actions
    handlePlayerClick,
    handleAddPlayer
  };

  // ========================================
  // MAIN RENDER
  // ========================================
  return (
    <div>
      {/* Bulk Add Banner (Fixed to top when selections exist) */}
      <MobileBulkAddBanner
        rosterCapacityAnalysis={positionAssignment.rosterCapacityAnalysis}
        onBatchAdd={() => {
          const selectedPlayers = getSelectedPlayerObjects(playerData.players);
          handleBatchAddPlayers(selectedPlayers);
        }}
      />

      {/* Status Banners (Errors, Success Messages) */}
      <MobileStatusBanners
        state={freeAgentState}
        isCommissionerMode={isCommissionerMode}
        activeTeamName={activeTeamName}
      />

      {/* Main Content */}
      {(leagueContext.transactionsEnabled || leagueContext.browseMode) && (
        <>
          <MobileSearchControls
            state={freeAgentState}
            leagueId={leagueId}
            isCommissionerMode={isCommissionerMode}
            activeTeamName={activeTeamName}
          />

          <MobilePlayerTable
            state={freeAgentState}
            leagueId={leagueId}
            league={assignmentLeague}
            isCommissionerMode={isCommissionerMode}
            activeTeamName={activeTeamName}
            activeTeamId={activeTeamId}
            handlePlayerClick={handlePlayerClick}
          />
        </>
      )}

      {/* POSITION ASSIGNMENT MODALS */}

      {/* Single Player Assignment - Mobile Modal */}
      {(() => {
      })()}
      <MobilePositionAssignmentModal
        player={positionAssignment.playerForAssignment}
        league={assignmentLeague}
        currentRoster={positionAssignment.currentRoster}
        onAssign={(assignmentData) => {
          const player = assignmentData.player || positionAssignment.playerForAssignment;
          if (!player) {
            return;
          }

          // Bible Rule 9: missing required data is a bug — surface it loudly.
          const rosterStatus = assignmentData.roster_status;
          if (!rosterStatus) {
            playerData.setError('Cannot add player: modal returned no roster_status');
            return;
          }
          // Position can come either as canonical roster_position or as `assignment`
          // slot id — both are valid keys for the same data, not a silent default.
          const rosterPosition = assignmentData.roster_position ?? assignmentData.assignment;

          let position = 'bench';
          let slotId = 'bench';

          if (rosterStatus === 'minors') {
            position = 'minors';
            slotId = 'minors';
          } else if (rosterStatus === 'bench') {
            position = 'bench';
            slotId = 'bench';
          } else if (rosterStatus === 'active' && rosterPosition) {
            // Extract base position from slot ID (e.g., 'C_0' -> 'C')
            position = rosterPosition.split('_')[0];
            slotId = rosterPosition;
          } else if (rosterStatus === 'active') {
            playerData.setError('Cannot add to active roster: modal returned no slot id');
            return;
          }

          executePlayerAssignment(player, {
            position,
            slotId,
            type: rosterStatus,
            reason: 'Manual selection',
            priority: 1
          });
        }}
        onCancel={() => {
          positionAssignment.setShowPositionModal(false);
          positionAssignment.setPlayerForAssignment(null);
        }}
        isVisible={positionAssignment.showPositionModal}
        mode="add"
        // Phase 2.3: 5th button "Drop a player" entry point.
        // Per ROSTER_PLAN_SPEC.md § B.5.1 + § B.5.7 patched
        // condition. Closes this modal and opens
        // MobileRosterPlanModal. The 5th button only renders when
        // active full + (bench/DL/minors has room).
        onOpenDropShuffle={() => {
          positionAssignment.setShowPositionModal(false);
          rosterPlan.open();
        }}
      />

      {/* Phase 2.3: Mobile drop-shuffle plan modal. Per § B.5.7. */}
      <MobileRosterPlanModal
        candidate={positionAssignment.playerForAssignment}
        currentRoster={positionAssignment.currentRoster}
        state={rosterPlan.state}
        plans={rosterPlan.plans}
        selectedDropPlayer={rosterPlan.selectedDropPlayer}
        selectedPlan={rosterPlan.selectedPlan}
        errorClass={rosterPlan.errorClass}
        errorMessage={rosterPlan.errorMessage}
        infeasibleReason={rosterPlan.infeasibleReason}
        onPickDrop={rosterPlan.requestPlans}
        onChangeDrop={rosterPlan.changeDrop}
        onSelectPlan={rosterPlan.selectPlan}
        onConfirm={isWaiverPlanQueueMode ? handleWaiverPlanQueueConfirm : rosterPlan.executePlan}
        onCancel={() => {
          rosterPlan.cancel();
          rosterPlan.reset();
          positionAssignment.setPlayerForAssignment(null);
          if (isWaiverPlanQueueMode) {
            setIsWaiverPlanQueueMode(false);
            setWaiverPlayerPending(null);
          }
        }}
        onRetry={rosterPlan.retry}
        onDone={() => {
          // After successful execution, close cleanly. Executor's
          // roster-updated dispatch (in useRosterPlan) triggers
          // useMobilePlayerAssignment's listener → roster refetch.
          rosterPlan.reset();
          positionAssignment.setPlayerForAssignment(null);
          if (isWaiverPlanQueueMode) {
            setIsWaiverPlanQueueMode(false);
            setWaiverPlayerPending(null);
          }
        }}
        isVisible={
          rosterPlan.state !== 'idle' && rosterPlan.state !== 'cancelled'
        }
      />

      {/* Waiver Claim Modal — opened when user taps Claim on a waiver player.
          isRosterFull/hasActiveSlot/hasBenchSlot excludes DL+minors via
          canClaimWaiverPlayer (waiver awards must land on active or bench). */}
      {(() => {
        const claim = waiverPlayerPending && positionAssignment.rosterCapacityAnalysis
          ? canClaimWaiverPlayer(waiverPlayerPending, positionAssignment.rosterCapacityAnalysis)
          : null;
        return (
          <MobileDropDesignationModal
            isOpen={showWaiverModal}
            onClose={() => {
              setShowWaiverModal(false);
              setWaiverPlayerPending(null);
            }}
            onConfirm={handleWaiverClaimConfirm}
            onRequestPlan={handleWaiverRequestPlan}
            playerBeingAdded={waiverPlayerPending}
            currentRoster={positionAssignment.rosterCapacityAnalysis?.players || []}
            leagueId={leagueId}
            context="waiver_claim"
            isRosterFull={claim ? !claim.canClaim : true}
            hasActiveSlot={claim ? claim.hasActiveSlot : null}
            hasBenchSlot={claim ? claim.hasBenchSlot : null}
            waiverPriority={null}
            totalTeams={null}
            waiverExpiresAt={
              waiverPlayerPending?.league_context?.availability?.waiver_expires_at || null
            }
          />
        );
      })()}

      {/* Bulk Player Assignment */}
      <MobileBulkPositionAssignmentModal
        selectedPlayers={getSelectedPlayerObjects(playerData.players)}
        league={league}
        capacityAnalysis={positionAssignment.rosterCapacityAnalysis}
        currentRoster={positionAssignment.currentRoster}
        onAssignAll={handleBulkAssignmentComplete}
        onCancel={() => positionAssignment.setShowBulkAssignmentModal(false)}
        isVisible={positionAssignment.showBulkAssignmentModal}
      />

      {/* PLAYER MODAL */}
      <MobilePlayerModal
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerModalOpen}
        onClose={handleClosePlayer}
        onExpandToCard={handleExpandToCard}
        onSwap={handleSwapInitiate}
      />

      {/* DD-RO-MOBILE-SWAP-0727: the FA-direction swap — pick the roster player to drop, then confirm the 1-1
          swap in MobileFASwapSheet. Both reuse existing components; no new mutation path. */}
      <MobileDropDesignationModal
        isOpen={swapDropOpen}
        onClose={() => { setSwapDropOpen(false); setSwapCandidate(null); }}
        onConfirm={handleSwapDropConfirm}
        playerBeingAdded={swapCandidate}
        currentRoster={positionAssignment.rosterCapacityAnalysis?.players || []}
        leagueId={leagueId}
        context="fa_add"
        isRosterFull={true}
        hasActiveSlot={null}
        hasBenchSlot={null}
      />
      {swapRec && (
        <MobileFASwapSheet
          rec={swapRec}
          leagueId={leagueId}
          onClose={() => setSwapRec(null)}
          onComplete={() => setSwapRec(null)}
        />
      )}

      {/* PLAYER CARD */}
      <MobilePlayerCard
        playerId={selectedPlayerId}
        leagueId={leagueId}
        isOpen={isPlayerCardOpen}
        onClose={handleClosePlayer}
        onCollapse={handleCollapseToModal}
      />
    </div>
  );
};

export default MobileFreeAgentSearch;
